import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import moment from 'moment';
import { formatarData } from 'src/app/core/utils/utilities';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { EstoqueService } from 'src/app/services/EstoqueService/estoque.service';
import { IEstoqueRelatorioFilter, IEstoqueRelatorioVendaItem } from 'src/app/services/EstoqueService/estoque.model';
import { IProduto } from 'src/app/services/ProdutoService/produto.model';
import { ProdutoService } from 'src/app/services/ProdutoService/produto.service';

type IOptions = { name: string; value: number | boolean | null };

@Component({
  selector: 'app-relatorio-estoque-vendas',
  templateUrl: './relatorio-estoque-vendas.component.html',
  styleUrls: ['./relatorio-estoque-vendas.component.scss']
})
export class RelatorioEstoqueVendasComponent implements OnInit, AfterViewInit {
  form = this.fb.group({
    DT_INIC: [null as string | null],
    DT_FIM: [null as string | null],
    FK_PROD: [0],
    FL_CNCL: [null as boolean | null]
  });

  isLoading = false;
  tableData: Array<any> = [];
  valorTotal = 0;

  produtoOptions: IOptions[] = [{ name: 'Todos', value: 0 }];
  cancelamentoOptions: IOptions[] = [
    { name: 'Todos', value: null },
    { name: 'Ativas', value: false },
    { name: 'Canceladas', value: true }
  ];

  tableColumns = [
    { header: 'Data', key: 'data', type: 'text' },
    { header: 'Produto', key: 'produto', type: 'text' },
    { header: 'Quantidade', key: 'quantidade', type: 'text' },
    { header: 'Vl Unit.', key: 'valorUnit', type: 'text' },
    { header: 'Vl Total', key: 'valorTotal', type: 'text' },
    { header: 'Vl Final', key: 'valorFinal', type: 'text' },
    { header: 'Operador', key: 'operador', type: 'text' },
    { header: 'Cancelada', key: 'cancelada', type: 'text' }
  ];

  constructor(
    private fb: FormBuilder,
    private estoqueService: EstoqueService,
    private produtoService: ProdutoService,
    private appExportBase64: AppExportBase64
  ) {
    const fim = moment().endOf('day');
    const inicio = moment().subtract(3, 'months').startOf('month');
    this.form.patchValue({
      DT_INIC: inicio.format('YYYY-MM-DDTHH:mm'),
      DT_FIM: fim.format('YYYY-MM-DDTHH:mm')
    });
  }

  ngOnInit(): void {
    this.loadProdutos();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => this.loadData());
  }

  loadProdutos(): void {
    this.produtoService.GetProdutos().subscribe({
      next: (produtos: IProduto[]) => {
        this.produtoOptions = [
          { name: 'Todos', value: 0 },
          ...produtos.map(p => ({ name: p.NM_PROD, value: p.PK_PROD }))
        ];
      }
    });
  }

  onSelectChange(event: number | boolean | null, name: 'FK_PROD' | 'FL_CNCL'): void {
    this.form.controls[name].setValue(event as any);
  }

  private createFilter(): IEstoqueRelatorioFilter {
    const filter: IEstoqueRelatorioFilter = {
      DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
      DT_FIM: this.form.controls.DT_FIM.value ?? undefined
    };
    const fkProd = Number(this.form.controls.FK_PROD.value);
    if (fkProd > 0) filter.FK_PROD = fkProd;
    const flCncl = this.form.controls.FL_CNCL.value;
    if (flCncl !== null && flCncl !== undefined) filter.FL_CNCL = flCncl as boolean;
    return filter;
  }

  loadData(): void {
    this.isLoading = true;
    this.estoqueService.GetRelatorioVendas(this.createFilter()).subscribe({
      next: (items: IEstoqueRelatorioVendaItem[]) => {
        this.valorTotal = items.reduce(
          (acc, item) => acc + Number(item.VL_FINAL ?? item.VL_TOTL),
          0
        );
        this.tableData = items.map(item => ({
          data: formatarData(item.DT_CRIA),
          produto: item.NM_PROD,
          quantidade: item.QT_VEND,
          valorUnit: Number(item.VL_UNIT).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }),
          valorTotal: Number(item.VL_TOTL).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }),
          valorFinal: Number(item.VL_FINAL).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }),
          operador: item.NM_FUNC_OPER ?? '---',
          cancelada: item.FL_CNCL ? 'Sim' : 'Não'
        }));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarPdfPorCliente(): void {
    this.isLoading = true;
    const filter = this.createFilter();
    // Relatório clássico considera vendas ativas quando situação = Todos
    if (filter.FL_CNCL === undefined) {
      filter.FL_CNCL = false;
    }
    this.estoqueService.GetReportVendasPorClientePdf(filter).subscribe({
      next: response => {
        this.appExportBase64.downloadPDF(
          response.ReportBase64,
          'produtos-vendidos-por-cliente'
        );
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarExcel(): void {
    this.isLoading = true;
    const filter = this.createFilter();
    this.estoqueService
      .GetReportExcel({
        ReportType: 'vendas',
        DT_INIC: filter.DT_INIC,
        DT_FIM: filter.DT_FIM,
        FK_PROD: filter.FK_PROD,
        FL_CNCL: filter.FL_CNCL
      })
      .subscribe({
        next: response => {
          this.appExportBase64.downloadExcel(response.ReportBase64, 'vendas-estoque');
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  get valorTotalFormatado(): string {
    return this.valorTotal.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }
}
